import type { ComponentType, ReactNode } from "react";
import { useParams } from "react-router";

import { PageNotAvailableContent, PrimaryButton } from "@skilitics/designer-ui-components";
import { FormattedMessage } from "@skilitics/localization";

import { NodeDesigner } from "../NodeDesigner";
import type { DesignerNodeHandle } from "../types";
import type { NodeDesignerProps } from "../useNodeDesignerComponentProps";

type Props = Omit<NodeDesignerProps, "node"> & {
  /** nodes to choose from. the one with the id from the route is rendered */
  nodes: DesignerNodeHandle[];
  /** Wrapper component (optional) */
  component?: ComponentType<{ node: DesignerNodeHandle; children: ReactNode }>;
};

export const NodeDesignerFromRoute = ({ nodes, component: WrapperComponent, ...props }: Props) => {
  const { nodeId: nodeIdStr } = useParams<"nodeId">();

  const nodeId = Number(nodeIdStr);
  const node = nodes.find((n) => n.id === nodeId);

  if (!node) {
    return (
      <PageNotAvailableContent>
        <p>
          <FormattedMessage messages={formatStrings.notFoundMessage} values={{ nodeId }} />
        </p>
        <p>
          <FormattedMessage messages={formatStrings.navigationMessage} />
        </p>
        <PrimaryButton href="/">
          <FormattedMessage messages={formatStrings.home} />
        </PrimaryButton>
      </PageNotAvailableContent>
    );
  }
  if (WrapperComponent) {
    return (
      <WrapperComponent node={node}>
        <NodeDesigner node={node} {...props} />
      </WrapperComponent>
    );
  }
  return <NodeDesigner key={nodeId} node={node} {...props} />;
};

const formatStrings = {
  home: {
    en: "Dashboard",
  },
  notFoundMessage: {
    en: "Unable to find node with ID: {nodeId}.",
  },
  navigationMessage: {
    en: "Use the breadcrumb navigation in the header, or return to the home page:",
  },
};
